import React, { PropsWithChildren, useEffect, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import step1 from './../../assets/images/nextSteps/icon-circle-blue-1.svg';
import step2 from './../../assets/images/nextSteps/icon-circle-blue-2.svg';
import step3 from './../../assets/images/nextSteps/icon-circle-blue-3.svg';
import step4 from './../../assets/images/nextSteps/icon-circle-blue-4.svg';
import step5 from './../../assets/images/nextSteps/icon-circle-blue-5.svg';
import step6 from './../../assets/images/nextSteps/icon-circle-blue-6.svg';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import axios from 'axios';
import SessionStorage from '../../utils/SessionStorage';
import './NextSteps.css';
import { ChaContentResponse } from '../../types/responses/ChaContentResponse';
import { AcquisitionFlow, getAcquisitionFlow } from '../../utils/acquisition-utils';
import useGenerateAcquisitionsPdf from '../../hooks/useGenerateAcquisitionsPdf';
import { Row, Col } from '../../importCL';
interface INextStepsProps extends MappedComponentProperties {
    usingNewEndpoint?: boolean;
    version?: string;
    headline?: string;
    unavailableText?: string;
    nonSecuredSteps?: string[];
    // secured card - micro deposits was removed
    securedCardNoMdSteps?: string[];
}

interface steps {
    [key: string]: string[] | undefined;
}

export const NextStepsEditConfig = {
    emptyLabel: 'Next Steps',
    isEmpty: (props: PropsWithChildren<INextStepsProps>) => {
        return !props || !props.headline;
    },
};


const NextSteps = (props: PropsWithChildren<INextStepsProps>): JSX.Element => {
    const [allScenariosSteps, setAllScenariosSteps] = useState<steps>({
        securedCardNonMd: props.securedCardNoMdSteps,
        nonSecured: props.nonSecuredSteps,
    });
    const steps = [step1, step2, step3, step4, step5, step6];
    const isEsign = SessionStorage.tryGetObject('isEsign', false);
    const [version, setVersion] = useState<string>('');
    const [pdfInfo, setPdfInfo] = useState<Record<string, any>>({
        path: '',
        title: '',
    });
    const isProduction = window.location.hostname.includes('creditone');
    const { generate: generatePdf } = useGenerateAcquisitionsPdf();

    const setStepsVersion = (prequalFullAppData: Record<string, any>) => {
        if (!isProduction) {
            setVersion(props.version || 'nonSecured');
        } else if (!prequalFullAppData || !prequalFullAppData?.IsSecuredCard) {
            setVersion('nonSecured');
        } else {
            setVersion('securedCardNonMd');
        }
    };

    const populatePdf = (applicationData: Record<string, any>, signal?: AbortSignal) => {
        if (applicationData && isEsign) {
            const acqFlow = {
                [AcquisitionFlow.PQ]: 'preQualification',
                [AcquisitionFlow.PA]: 'preApproved',
                [AcquisitionFlow.SkipPQ]: 'skipPreQualification',
                [AcquisitionFlow.PqOfOne]: 'skipPreQualification',
                [AcquisitionFlow.PHA]: 'partnerHosted',
            }[getAcquisitionFlow()];
            const programCode = SessionStorage.get('selectedProgramCode');
            const creditLine = acqFlow === 'partnerHosted' ? applicationData?.card?.creditLine : applicationData.CreditLine?.replace('$', '');

            axios.get<ChaContentResponse>(`/services/chaContent${props.usingNewEndpoint ? 'v2' : ''}?flowType=${acqFlow}&programCode=${programCode}&creditLine=${creditLine}`,
                { signal: signal },
            )
                .then((response) => {                    
                    if (response?.data) {
                        setPdfInfo({
                            path: response.data.path,
                            title: response.data.title,
                            staticPdf: response.data.addOnPdf,
                            pdfOptions: response.data.pdfOptions,
                        });
                    }
                }).catch(err => {
                    if (axios.isCancel(err)) {
                        console.debug('Call Canceled: ' + err);
                    }
                });
        }
    };

    const esignText = () => {
        if (isEsign) {
            if (pdfInfo.path) {
                return (
                    <Col className='NextSteps__pdf' xs={12}>
                        <a target='_blank' className='NextSteps__pdfLink' {...(pdfInfo.path.includes('.pdf') ? { href: pdfInfo.path }
                            : { onClick: () => generatePdf(pdfInfo.staticPdf, pdfInfo.path, pdfInfo.pdfOptions) })}>{pdfInfo.title}</a>
                    </Col>
                );
            }
            return (
                <Col className='NextSteps__unavailableText' xs={12}>
                    {props.unavailableText &&
                        transformHTMLParserLinks(props.unavailableText)
                    }
                </Col>
            );
        }
    };

    const fetchNonSecuredData = async () => {
        const programCode = SessionStorage.get('selectedProgramCode');
        
        if (programCode) {
            try {
                const result = await axios.get(`/services/NextStepsContent?programCode=${programCode}`);
                if (result?.data?.content) {
                    setAllScenariosSteps({
                        securedCardNonMd: props.securedCardNoMdSteps,
                        nonSecured: result.data.content,
                    });
                }
            } catch (error) {
                console.error('Failed to retrieve content from the NextStepsContent servlet: ', error);
            }
        } else {
            console.debug('Program code does not exists, can not retrieve data from servlet');
        }
    };

    useEffect(() => {
        if (version === 'nonSecured' && window.location.href.includes('application-approved')) {
            fetchNonSecuredData();
        }
    }, [version]);


    useEffect(() => {
        const abortController = new AbortController();
        const initializeSteps = async () => {
            
            const signal = abortController.signal;

            if (getAcquisitionFlow() === AcquisitionFlow.PHA) {
                setVersion(props.version);

                const flowData = SessionStorage.tryGetObject('flowApplicationData', null);
                
                // do not call for pending status
                if (flowData !== null && !window.location.href.includes("pending")) {
                    populatePdf(flowData, signal);
                }
            } else {
                const prequalFullAppData = await SessionStorage.tryGetCipher('prequalFullAppData', undefined);
                const approvedData = await SessionStorage.tryGetCipher('app-approved-data', null);
                const flowData = SessionStorage.tryGetObject('flowApplicationData', null);
                setStepsVersion(prequalFullAppData);
                
                // do not call for pending status
                if (approvedData !== null && !window.location.href.includes("pending")) {
                    populatePdf(approvedData, signal);
                }
                // do not call for pending status
                else if (flowData !== null && !window.location.href.includes("pending")) {
                    populatePdf(flowData, signal);
                }
            }
        };

        initializeSteps();

        return () => {
            abortController.abort();
        };
    }, [props.version]);


    return (
        <Row id='next-steps' className='NextSteps'>
            <Col xs={12}>
                {props.headline &&
                    <h6 className='NextSteps__headline'>{transformHTMLParserLinks(props.headline)}</h6>
                }
                {allScenariosSteps[version]?.map((step: string, i: number) => {
                    return (
                        step &&
                        <div key={i} className='NextSteps__stepsRow'>
                            <div className='NextSteps__numbersCol'>
                                <img loading='lazy' src={steps[i]} alt={`Step ${i + 1}`} />
                            </div>
                            <div className='NextSteps__textCol'>{transformHTMLParserLinks(step)}</div>
                        </div>
                    );
                })}
            </Col>
            {esignText()}
        </Row>

    );
};

export default NextSteps;
